import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosRequestHeaders,
  type InternalAxiosRequestConfig,
} from "axios";
import { env } from "./env";
import { useAuthStore } from "../store/auth-store";
import type { User } from "../types/user";

interface SessionResponse {
  user: User;
}

interface RetryRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const isAuthSessionEndpoint = (url?: string) =>
  typeof url === "string" &&
  [
    "/auth/login",
    "/auth/refresh",
    "/auth/logout",
    "/auth/forgot-password",
    "/auth/reset-password",
  ].some((path) => url.includes(path));

export const apiClient = axios.create({
  baseURL: env.apiUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const refreshClient = axios.create({
  baseURL: env.apiUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const removeContentTypeHeader = (
  headers: AxiosRequestHeaders | undefined,
): AxiosRequestHeaders => {
  if (headers instanceof AxiosHeaders) {
    headers.delete("Content-Type");
    return headers;
  }

  const next = new AxiosHeaders(headers);
  next.delete("Content-Type");
  return next;
};

apiClient.interceptors.request.use((config) => {
  const isFormDataPayload =
    typeof FormData !== "undefined" && config.data instanceof FormData;

  if (isFormDataPayload) {
    config.headers = removeContentTypeHeader(
      config.headers as AxiosRequestHeaders | undefined,
    );
  }

  return config;
});

let refreshRequest: Promise<SessionResponse> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryRequestConfig | undefined;
    const status = error.response?.status;

    if (!originalRequest || status !== 401 || originalRequest._retry) {
      if (status === 401 && !isAuthSessionEndpoint(originalRequest?.url)) {
        useAuthStore.getState().clearAuth();
      }
      return Promise.reject(error);
    }

    if (isAuthSessionEndpoint(originalRequest.url)) {
      useAuthStore.getState().clearAuth();
      return Promise.reject(error);
    }

    if (!refreshRequest) {
      refreshRequest = refreshClient
        .post<SessionResponse>("/auth/refresh", {})
        .then((response) => response.data)
        .finally(() => {
          refreshRequest = null;
        });
    }

    try {
      const data = await refreshRequest;
      if (data.user) {
        useAuthStore.getState().setAuth({ user: data.user });
      }

      originalRequest._retry = true;
      return apiClient(originalRequest);
    } catch (refreshError) {
      useAuthStore.getState().clearAuth();
      return Promise.reject(refreshError);
    }
  },
);
